import { useState, useEffect } from "react";
import { Save, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminSettings() {
  const [form, setForm] = useState({ app_name: "", razorpay_key: "", razorpay_secret: "", support_email: "", support_phone: "", anti_piracy_enabled: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.get("/settings/admin").then((r) => {
      if (r.data) {
        setForm(prev => ({
          app_name: r.data.app_name || prev.app_name,
          razorpay_key: r.data.razorpay_key || prev.razorpay_key,
          razorpay_secret: r.data.razorpay_secret || prev.razorpay_secret,
          support_email: r.data.support_email || prev.support_email,
          support_phone: r.data.support_phone || prev.support_phone,
          anti_piracy_enabled: r.data.anti_piracy_enabled === true,
        }));
      }
    }).catch((err) => console.error(err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await api.put("/settings", form);
      setMessage("Settings saved successfully!");
    } catch (err) {
      setMessage("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <h1 className="text-2xl font-black admin-text mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Settings</h1>

        {message && (
          <div className={`mb-4 px-4 py-2 text-sm font-medium ${
            message.includes("success") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
          }`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white p-4 space-y-4" data-testid="settings-form">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">App Name</label>
            <input data-testid="settings-app-name" value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Razorpay Key ID</label>
            <input data-testid="settings-razorpay-key" value={form.razorpay_key} onChange={(e) => setForm({ ...form, razorpay_key: e.target.value })} className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Razorpay Secret</label>
            <input data-testid="settings-razorpay-secret" type="password" value={form.razorpay_secret} onChange={(e) => setForm({ ...form, razorpay_secret: e.target.value })} className={inputClass} placeholder="Enter secret for live payments" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Support Email</label>
            <input data-testid="settings-support-email" type="email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Support Phone</label>
            <input data-testid="settings-support-phone" value={form.support_phone} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} className={inputClass} />
          </div>

          {/* Anti-Piracy Toggle */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${form.anti_piracy_enabled ? "bg-red-500/10" : "bg-slate-100"}`}>
                  <ShieldAlert size={20} className={form.anti_piracy_enabled ? "text-red-500" : "text-slate-400"} />
                </div>
                <div>
                  <p className="text-sm font-bold admin-text">Enable Anti-Piracy Protection</p>
                  <p className="text-[11px] admin-text-secondary">Video watermarks and recording protection for students. Teachers & Admins are unaffected. PDF notes remain freely downloadable.</p>
                </div>
              </div>
              <button
                type="button"
                data-testid="anti-piracy-toggle"
                onClick={() => setForm({ ...form, anti_piracy_enabled: !form.anti_piracy_enabled })}
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${form.anti_piracy_enabled ? "bg-red-500" : "bg-slate-300"}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${form.anti_piracy_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {form.anti_piracy_enabled && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-[11px] text-red-700 font-medium">Protection active for Students (Video only):</p>
                <ul className="text-[10px] text-red-600 mt-1 space-y-0.5 ml-3 list-disc">
                  <li>Dynamic moving watermark (name, phone, timestamp)</li>
                  <li>Right-click, download, PiP, shortcuts disabled on video</li>
                  <li>Tab-switch detection pauses video with warning</li>
                </ul>
                <p className="text-[10px] text-green-700 mt-2 font-medium">PDF notes: Free download for all students (no restrictions)</p>
              </div>
            )}
          </div>
          <button data-testid="settings-save-btn" type="submit" disabled={saving} className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none flex items-center justify-center gap-2">
            <Save size={16} />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </form>
      </div>
    </AdminLayout>
  );
}
